import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  OnInit,
  Output,
  forwardRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgControl } from '@angular/forms';
import { AddressType, filterNullUndefined } from '@common';
import { TranslatePipe } from '@i18n';
import { QMSanitiseDirective } from '@qm';
import { BehaviorSubject, Observable, concat, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  finalize,
  map,
  skip,
  switchMap,
} from 'rxjs/operators';
import { SelectTypeaheadComponent } from '../../../select/components/select-typeahead/select-typeahead.component';
import { BaseFormControlComponent } from '../../../shared/components/base-form-control/base-form-control.component';
import { FormOption } from '../../../shared/models/form-option.interface';
import { toFormOption } from '../../../shared/operators/to-form-option/to-form-option';
import {
  disableFormControls,
  enableFormControls,
} from '../../../shared/utils/form-control/form-control.utils';
import { AddressSearchResultOverview } from '../../models/address-search-result/address-search-result-overview.interface';
import { AddressSearchResult } from '../../models/address-search-result/address-search-result.interface';
import { AddressService } from '../../services/address/address.service';
import { formatAddressSearchResultToOverview } from '../../utils/address.utils';

@Component({
  selector: 'app-form-address-typeahead',
  templateUrl: './address-typeahead.component.html',
  providers: [
    {
      provide: BaseFormControlComponent,
      useExisting: forwardRef(() => AddressTypeaheadComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    QMSanitiseDirective,
    SelectTypeaheadComponent,
    TranslatePipe,
  ],
  standalone: true,
})
export class AddressTypeaheadComponent
  extends BaseFormControlComponent<string>
  implements OnInit
{
  addressOptions$ = new BehaviorSubject<FormOption[]>([]);

  private addresses$ = new BehaviorSubject<AddressSearchResultOverview[]>([]);
  private selectedAddress$ = new BehaviorSubject<
    AddressSearchResultOverview | undefined
  >(undefined);
  private searchTerm$ = new BehaviorSubject<string>('');

  @Output() addressSelected = new EventEmitter<AddressSearchResult>();
  private readonly addressService = inject(AddressService);
  protected override readonly changeDetectorRef = inject(ChangeDetectorRef);
  protected override readonly ngControl = inject(NgControl);
  protected override readonly destroyRef = inject(DestroyRef);

  constructor() {
    super();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.buildAddresses();
    this.buildAddressOptions();
    this.listenToSelectedAddress();
    this.listenToEnabledChanges();
  }

  onSearch(searchTerm: string): void {
    this.searchTerm$.next(searchTerm);
  }

  onOptionSelected(option: FormOption): void {
    const selectedAddress: AddressSearchResultOverview | undefined =
      this.addresses$.value.find(
        (addressOverview: AddressSearchResultOverview) =>
          addressOverview.Id === option.value,
      );
    this.selectedAddress$.next(selectedAddress);
  }

  private buildAddresses(): void {
    concat(this.loadPreviouslySelectedAddress(), this.listenToSearchTerm())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((addresses: AddressSearchResultOverview[]) => {
        this.addresses$.next(addresses);
        this.changeDetectorRef.markForCheck();
      });
  }

  private buildAddressOptions(): void {
    this.addresses$
      .pipe(
        toFormOption({
          selectLabel: (addressOverview: AddressSearchResultOverview) =>
            `${addressOverview.Text}, ${addressOverview.Description}`,
          selectValue: 'Id',
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((addressOptions: FormOption[]) => {
        this.addressOptions$.next(addressOptions);
        this.changeDetectorRef.markForCheck();
      });
  }

  private listenToSearchTerm(): Observable<AddressSearchResultOverview[]> {
    return this.searchTerm$.pipe(
      skip(1),
      distinctUntilChanged(),
      switchMap((searchTerm?: string) =>
        this.addressService.searchAddress(searchTerm),
      ),
    );
  }

  private listenToSelectedAddress(): void {
    this.selectedAddress$
      .pipe(
        filterNullUndefined(),
        distinctUntilKeyChanged('Id'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((selectedAddress: AddressSearchResultOverview) => {
        if (
          selectedAddress.Type === AddressType.Address ||
          selectedAddress.Type === AddressType.Commercial
        ) {
          this.setValue(selectedAddress.Id);
        } else {
          this.searchAddressByContainerId(selectedAddress.Id);
        }

        this.changeDetectorRef.markForCheck();
      });
  }

  private loadPreviouslySelectedAddress(): Observable<
    AddressSearchResultOverview[]
  > {
    if (!this.control.value) {
      return of([]);
    }

    disableFormControls(this.control);
    return this.addressService.getAddressById(this.control.value).pipe(
      map((address: AddressSearchResult) => [
        formatAddressSearchResultToOverview(address),
      ]),
      finalize(() => enableFormControls(this.control)),
      catchError(() => {
        this.clear();
        return of([]);
      }),
    );
  }

  private searchAddressByContainerId(containerId: string): void {
    disableFormControls(this.control);

    this.addressService
      .searchAddress(this.searchTerm$.value, containerId)
      .pipe(
        finalize(() => enableFormControls(this.control)),
        catchError(() => of([])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((addresses: AddressSearchResultOverview[]) => {
        this.addresses$.next(addresses);
        this.changeDetectorRef.markForCheck();
      });
  }

  private listenToEnabledChanges(): void {
    this.enabledChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.changeDetectorRef.markForCheck();
      });
  }
}
